// httppackinfomanager.ts ↔ mpshared/packinfomanager.cpp (PackInfoManager — the
// pack-info JSON cache) + game/httppackinfomanager.cpp (HttpPackInfoManager —
// the HTTP fetch arm, gppim).
//
// The info wire shape (confirmed against the live content worker — GET
// /wi/info/<id8>-<md5>): a JSON map; the client reads "id" (number or decimal
// string), "h" (md5 hex32), "t" (title), "a" (author), "d" (description),
// "di" (discuss URL).
//
// SHAPE NOTES (same seams as httppackmanager.ts):
//  - the info cache directory is an injected PackStore (keys =
//    GetInfoFilename's "iiiiiiii-hhhh…32"); a SEPARATE store instance from the
//    pack cache (the C++ uses separate cachedirs);
//  - the temp file collapses to a chunk list;
//  - LoadInfoMap's JsonBuilder-over-fread becomes JSON.parse over the bytes
//    (the C++ requires the root be a map — JSONTYPE_MAP — mirrored).

import type { PackId } from '../io/savegame.ts';
import {
  ToHex,
  FromHex,
  ToDword,
  ToInteger,
  kszPackInfoUrl,
  kdwClientID,
  knVersionSimulation,
  HeadersMap,
  HttpRequest,
  type HttpService,
  type HttpResponseHandler,
  type ProgressCallback,
} from './httpservice.ts';
import type { PackStore } from './httppackmanager.ts';

/** The parsed info json::JsonMap. */
export type InfoMap = Record<string, unknown>;

/** The read-only forum clone that replaced the long-dead phpBB board which used
 *  to live at www.warfareincorporated.com/xyzzy/ (the old URL now just lands on
 *  the marketing homepage). See cloudflare/scripts/forum/. */
export const kszForumCloneUrl = 'https://forum.warfareincorporated.com';

/** Remap a pack-info "di" (discuss) URL to the forum clone (issue #106). The
 *  stored links still point at the old board (".../xyzzy/viewtopic.php?t=<id>");
 *  the clone serves each topic at /topic/t<id> (cloudflare/scripts/forum/lib/
 *  util.mjs url.topic — the phpBB topic id is preserved, and equals the pack id).
 *  Other old-board links fall back to the clone home; anything that isn't an
 *  xyzzy board link (e.g. a future non-forum discuss URL) is returned unchanged. */
export function forumDiscussUrl(raw: string): string {
  if (!/\/xyzzy\//i.test(raw)) return raw;
  const m = /[?&]t=(\d+)/.exec(raw);
  if (m !== null && /viewtopic\.php/i.test(raw)) return `${kszForumCloneUrl}/topic/t${m[1]}`;
  return `${kszForumCloneUrl}/`;
}

//
// PackInfoManager — mpshared/packinfomanager.cpp
//

export class PackInfoManager {
  protected cachedir_: string;
  protected readonly store: PackStore;

  // mpshared/packinfomanager.cpp:8-10
  constructor(store: PackStore, cachedir = 'packinfos') {
    this.store = store;
    this.cachedir_ = cachedir;
  }

  // mpshared/packinfomanager.cpp:15-31 — the ppackid out param is mutated in
  // place (the C++ shape); false = missing/invalid id or h
  protected GetPackId(map: InfoMap, ppackid: PackId): boolean {
    const id = this.GetString(map, 'id');
    if (id === null) return false;
    const hash = this.GetString(map, 'h');
    if (hash === null) return false;
    if (!FromHex(hash, ppackid.hash, 16)) return false;
    const dw = ToDword(id, 10);
    if (dw === null) return false;
    ppackid.id = dw;
    return true;
  }

  // mpshared/packinfomanager.cpp:33-43 — strings pass through; numbers return
  // their lexeme (json::JsonNumber::GetString)
  protected GetString(map: InfoMap, key: string): string | null {
    const obj = map[key];
    if (obj === undefined || obj === null) return null;
    if (typeof obj === 'string') return obj;
    if (typeof obj === 'number') return String(obj);
    return null;
  }

  // mpshared/packinfomanager.cpp:45-53
  GetInfoMap(ppackid: PackId): InfoMap | null {
    const bytes = this.store.read(this.GetInfoFilename(ppackid));
    if (bytes === null) return null;
    return this.LoadInfoMap(bytes);
  }

  // mpshared/packinfomanager.cpp:55-73 — parse + require a JSON map root
  protected LoadInfoMap(bytes: Uint8Array): InfoMap | null {
    let obj: unknown;
    try {
      obj = JSON.parse(new TextDecoder().decode(bytes)) as unknown;
    } catch {
      return null;
    }
    if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) {
      return null;
    }
    return obj as InfoMap;
  }

  // mpshared/packinfomanager.cpp:75-80 — "%s/%08lx-%s"; the store IS the
  // cachedir, so the key drops the directory prefix (as PackManager's
  // GetPackFilename store keys)
  protected GetInfoFilename(ppackid: PackId): string {
    const szHash = ToHex(ppackid.hash, 16);
    return `${(ppackid.id >>> 0).toString(16).padStart(8, '0')}-${szHash}`;
  }
}

//
// HttpPackInfoManager — game/httppackinfomanager.cpp (gppim)
//

export class HttpPackInfoManager extends PackInfoManager implements HttpResponseHandler {
  private service_: HttpService | null;
  private ctx_: unknown = null;
  private callback_: ProgressCallback | null = null;
  private req_: HttpRequest | null = null;
  private code_ = 0;
  private error_ = false;
  private cbTotal_ = 0;
  private cbLength_ = 0;
  // the mkstemp temp file (tempfilename_/tempfile_/tempdir_)
  private chunks_: Uint8Array[] | null = null;
  private temppackid_: PackId = { id: 0, hash: new Uint8Array(16) };

  // game/httppackinfomanager.cpp:10-19
  constructor(service: HttpService | null, store: PackStore, cachedir = 'packinfos') {
    super(store, cachedir);
    this.service_ = service;
    this.Reset();
  }

  // game/httppackinfomanager.cpp:21-23 — ~HttpPackInfoManager
  Dispose(): void {
    this.Reset();
  }

  // game/httppackinfomanager.cpp:25-64
  Start(ppackid: PackId, ctx: unknown, callback: ProgressCallback | null): boolean {
    if (this.service_ === null) return false;

    // Initialize
    this.Reset();
    this.temppackid_ = { id: ppackid.id >>> 0, hash: ppackid.hash.slice() };
    this.ctx_ = ctx;
    this.callback_ = callback;

    // The temp file that will receive the bytes (mkstemp/fopen arms collapse)
    this.chunks_ = [];

    // Build the request
    this.req_ = this.service_.NewRequest(this);
    if (this.req_ === null) return false;
    this.SetServiceUrl(this.req_);

    // Submit the request. This will asynchronously call back.
    this.service_.SubmitRequest(this.req_);
    return true;
  }

  // game/httppackinfomanager.cpp:66-72 — "%s/%08x-%s?c=%d&v=%d"
  private SetServiceUrl(req: HttpRequest): void {
    const hash = ToHex(this.temppackid_.hash, 16);
    req.SetURL(
      `${kszPackInfoUrl}/` +
        `${(this.temppackid_.id >>> 0).toString(16).padStart(8, '0')}-${hash}` +
        `?c=${kdwClientID}&v=${knVersionSimulation}`,
    );
  }

  // game/httppackinfomanager.cpp:74-92
  Reset(): void {
    this.callback_ = null;
    this.ctx_ = null;
    this.code_ = 0;
    this.error_ = false;
    if (this.req_ !== null) {
      if (this.service_ !== null) {
        this.service_.ReleaseRequest(this.req_);
      }
      this.req_ = null;
    }
    this.cbTotal_ = 0;
    this.cbLength_ = 0;
    // fclose + unlink the temp file
    this.chunks_ = null;
  }

  // game/httppackinfomanager.cpp:94-124
  OnReceivedResponse(preq: HttpRequest, code: number, pheaders: HeadersMap | null): void {
    this.code_ = code;
    if (code >= 400) {
      // Http error
      this.error_ = true;
      if (this.callback_ !== null) {
        this.callback_.OnError(this.ctx_, `Server returned error ${code}`);
      }
      return;
    }
    if (code >= 200 && code < 300) {
      // Success! Get Content-Length and call back
      this.cbLength_ = -1;
      const szLength = pheaders?.GetValue('Content-Length') ?? null;
      if (szLength !== null) {
        const n = ToInteger(szLength, 10);
        if (n !== null) this.cbLength_ = n;
      }
      if (this.callback_ !== null) {
        this.callback_.OnBegin(this.ctx_, this.cbLength_);
      }
      this.cbTotal_ = 0;
      return;
    }
    // Ignore other status codes. If it's a redirect, OnReceivedResponse
    // will get called again.
    return;
  }

  // game/httppackinfomanager.cpp:126-145
  OnReceivedData(preq: HttpRequest, pbb: Uint8Array): void {
    if (this.error_) return;
    if (this.code_ >= 200 && this.code_ < 300) {
      this.chunks_!.push(pbb.slice());
      this.cbTotal_ += pbb.length;
      if (this.callback_ !== null) {
        this.callback_.OnProgress(this.ctx_, this.cbTotal_, this.cbLength_);
      }
    }
  }

  // game/httppackinfomanager.cpp:147-165
  OnFinishedLoading(_preq: HttpRequest): void {
    if (this.error_) {
      this.Reset();
      return;
    }
    if (this.code_ >= 200 && this.code_ < 300) {
      if (this.FinishInstall()) {
        if (this.callback_ !== null) {
          this.callback_.OnFinished(this.ctx_);
        }
      } else {
        if (this.callback_ !== null) {
          this.error_ = true;
          this.callback_.OnError(this.ctx_, 'Error parsing Mission Pack info!');
        }
      }
    }
    this.Reset();
  }

  // game/httppackinfomanager.cpp:167-175
  OnError(preq: HttpRequest, pszError: string): void {
    if (this.error_) return;
    this.error_ = true;
    if (this.callback_ !== null) {
      this.callback_.OnError(this.ctx_, pszError);
    }
  }

  // game/httppackinfomanager.cpp:177-211 — parse-validate, id/hash match,
  // then move into the cache
  private FinishInstall(): boolean {
    if (this.chunks_ === null) return false;

    // Assemble the temp file bytes
    let cb = 0;
    for (const c of this.chunks_) cb += c.length;
    const bytes = new Uint8Array(cb);
    let off = 0;
    for (const c of this.chunks_) {
      bytes.set(c, off);
      off += c.length;
    }
    this.chunks_ = null;

    // Build a json object to make sure it can be parsed
    const map = this.LoadInfoMap(bytes);
    if (map === null) {
      // unlink
      return false;
    }

    // Check that the id and hash matches
    const packid: PackId = { id: 0, hash: new Uint8Array(16) };
    if (
      !this.GetPackId(map, packid) ||
      packid.id >>> 0 !== this.temppackid_.id >>> 0 ||
      !packid.hash.every((b, i) => b === (this.temppackid_.hash[i] ?? 0))
    ) {
      // unlink
      return false;
    }

    // Move from temp directory to cache directory. Don't bother to delete
    // old info's with the same id (but different hash).
    this.store.write(this.GetInfoFilename(this.temppackid_), bytes);
    return true;
  }
}
